
import { SenderAddress } from "@/types/SenderAddress";
import {
  createSenderAddress,
  updateSenderAddress,
  getSenderAddressById,
  deleteSenderAddress,
  bulkDeleteSenderAddresses,
  getSenderAddressesByEvent,
  getPrimarySenderAddressForEvent,
  setPrimarySenderAddress,
  createTemplateSenderAddress,
  getSenderAddressesByTemplate,
  updateTemplateSenderAddress,
  setTemplatePrimarySenderAddress,
  deleteTemplateSenderAddress,
} from "@/lib/api/clients/SenderAddressClient";
import { handleErrorServer, isInsufficientCapability, ServerError } from "@/lib/api/errors/ErrorHandler";
import { isValidObjectId } from "@/lib/utils/input-validator";
import { getCookie } from "@/lib/auth/cookies";
import {
  getEmailDomainsByEventHandler,
  getEmailDomainsByCompanyHandler,
  getEmailDomainByIdHandler,
} from "./EmailDomainHandling";
import { EmailDomain } from "@/types/EmailDomain";

// Local part of a sender: byte-identical to the backend's
// `SenderAddressValidation.EMAIL_LOCAL_PART_PATTERN` (the source of truth).
// The old, stricter FE pattern refused `info-`, `a..b` and `100%` although the
// ADDRESS identity (same alphabet) had accepted them (audit 2026-09-24, M5).
const EMAIL_LOCAL_PART_REGEX = /^[A-Za-z0-9._%+-]{1,64}$/;
const DEFAULT_FIELDS = "senderAddressId,emailDomainId,eventId,email,primary,alias";

export async function createSenderAddressHandler(
  eventId: string,
  emailDomainId: string,
  email: string,
  alias?: string
): Promise<string | ServerError> {
  try {
    // Validate eventId
    if (!isValidObjectId(eventId)) {
      throw new Error("validationError");
    }

    // Validate emailDomainId
    if (!isValidObjectId(emailDomainId)) {
      throw new Error("validationError");
    }

    // Validate email
    if (!email || email.trim().length === 0) {
      throw new Error("validationError");
    }

    // Validate email format
    if (!EMAIL_LOCAL_PART_REGEX.test(email)) {
      throw new Error("validationError");
    }

    return await createSenderAddress(eventId, emailDomainId, email, alias);
  } catch (error) {
    console.error("Error creating sender address:", error);
    return handleErrorServer(error);
  }
}

export async function updateSenderAddressHandler(
  senderAddressId: string,
  eventId: string,
  alias: string | null
): Promise<void | ServerError> {
  try {
    if (!isValidObjectId(senderAddressId)) {
      throw new Error("validationError");
    }

    if (!isValidObjectId(eventId)) {
      throw new Error("validationError");
    }

    await updateSenderAddress(senderAddressId, eventId, alias);
  } catch (error) {
    console.error("Error updating sender address:", error);
    return handleErrorServer(error);
  }
}

export async function getSenderAddressByIdHandler(
  senderAddressId: string,
  eventId: string,
  fields: string = DEFAULT_FIELDS
): Promise<SenderAddress | ServerError> {
  try {
    // Validate senderAddressId
    if (!isValidObjectId(senderAddressId)) {
      throw new Error("validationError");
    }

    // Validate eventId
    if (!isValidObjectId(eventId)) {
      throw new Error("validationError");
    }

    // Validate fields
    if (!fields || fields.trim().length === 0) {
      throw new Error("validationError");
    }

    return await getSenderAddressById(
      senderAddressId,
      eventId,
      fields
    );
  } catch (error) {
    console.error("Error getting sender address by ID:", error);
    return handleErrorServer(error);
  }
}

export async function setPrimarySenderAddressHandler(
  senderAddressId: string,
  eventId: string
): Promise<void | ServerError> {
  try {
    // Validate senderAddressId
    if (!isValidObjectId(senderAddressId)) {
      throw new Error("validationError");
    }

    // Validate eventId
    if (!isValidObjectId(eventId)) {
      throw new Error("validationError");
    }

    await setPrimarySenderAddress(senderAddressId, eventId);
  } catch (error) {
    console.error("Error setting primary sender address:", error);
    return handleErrorServer(error);
  }
}

export async function deleteSenderAddressHandler(
  senderAddressId: string,
  eventId: string
): Promise<boolean | ServerError> {
  try {
    // Validate senderAddressId
    if (!isValidObjectId(senderAddressId)) {
      throw new Error("validationError");
    }

    // Validate eventId
    if (!isValidObjectId(eventId)) {
      throw new Error("validationError");
    }

    return await deleteSenderAddress(senderAddressId, eventId);
  } catch (error) {
    console.error("Error deleting sender address:", error);
    return handleErrorServer(error);
  }
}

export async function bulkDeleteSenderAddressesHandler(
  senderAddressIds: string[],
  eventId: string,
): Promise<void | ServerError> {
  if (!isValidObjectId(eventId)) return handleErrorServer("invalidEventId");
  try {
    await bulkDeleteSenderAddresses(senderAddressIds, eventId);
  } catch (error) {
    return handleErrorServer(error);
  }
}

/**
 * Every domain an event can send from, in one page.
 *
 * The handler's default is 10 and this call used to take it. From the 11th
 * domain on, a sender whose domain didn't make the page looked unresolvable —
 * it dropped out of the pickers and its address could not be composed, which
 * reads exactly like "this sender doesn't exist". 100 is the handler's own
 * ceiling (EmailDomainHandling.ts) and far above any real event's domain count.
 */
const DOMAIN_PAGE_LIMIT = 100;

/**
 * Sender + domain meta for an event.
 *
 * `senderAccessDenied` / `domainAccessDenied` exist because "you may not read
 * this" and "there is nothing to read" used to arrive as the SAME empty array.
 * A role without `sender-addresses:read` would then be told the event sends as
 * the platform default — a confident claim built on data the caller was never
 * allowed to see. Callers that display a resolved sender must stay silent when
 * these are set rather than resolve over an empty list.
 */
export type EmailsMetaInformation = {
  senderAddresses: SenderAddress[];
  emailDomains: EmailDomain[];
  senderAccessDenied: boolean;
  domainAccessDenied: boolean;
};

export async function getEmailsMetaInformation(
  eventId: string,
): Promise<EmailsMetaInformation | ServerError> {
  // No cookie company: both reads resolve the tenant from the event. The old
  // guard failed the whole page on a missing/unhydrated cookie, and the id it
  // passed was the LOGIN company — wrong on another company's event.
  try {
    const [senderAddresses, emailDomains] = await Promise.all([
      getSenderAddressesByEventHandler(eventId),
      drainDomainPages((page) =>
        getEmailDomainsByEventHandler(eventId, page, DOMAIN_PAGE_LIMIT),
      ),
    ]);

    // Sender/domain meta is auxiliary on the email-templates page: a role with
    // email-templates:read but not sender-addresses:read / email-domains:read
    // gets 403 `insufficientCapability` here. Degrade those to empty lists so
    // the templates list still renders; surface any other server error as before.
    const senderErr =
      "isServerError" in senderAddresses && senderAddresses.isServerError === true
        ? (senderAddresses as ServerError)
        : null;
    if (senderErr && !isInsufficientCapability(senderErr)) return senderErr;

    const domainErr =
      "isServerError" in emailDomains && emailDomains.isServerError === true
        ? (emailDomains as ServerError)
        : null;
    if (domainErr && !isInsufficientCapability(domainErr)) return domainErr;

    const senders = senderErr ? [] : (senderAddresses as SenderAddress[]);
    const domains = domainErr ? [] : (emailDomains as EmailDomain[]);

    return {
      senderAddresses: senders,
      emailDomains: await topUpMissingDomains(senders, domains, domainErr !== null),
      senderAccessDenied: senderErr !== null,
      domainAccessDenied: domainErr !== null,
    };
  } catch (error) {
    return handleErrorServer(error);
  }
}

/** Bound on the top-up below — a real event has a handful of domains, not 25. */
const DOMAIN_TOPUP_LIMIT = 25;

/**
 * Page cap for the domain drain. 5 × 100 = 500 domains for one company; beyond
 * that is a tenant-shaped anomaly worth seeing rather than silently draining.
 */
const DOMAIN_MAX_PAGES = 5;

/**
 * Read every page of a domain listing, not just the first.
 *
 * `DOMAIN_PAGE_LIMIT` covers every realistic tenant, so this loop almost always
 * makes exactly one request (page 0 comes back short and we stop). It exists for
 * the tenant that does exceed it, where the old single-page read silently
 * dropped domains — and with them, from every picker, the senders that
 * referenced them.
 *
 * Only sound since `$sort {_id:1}` was added to both backend aggregates: without
 * a stable order successive `$skip`/`$limit` pages may overlap and omit, so a
 * loop could finish "complete" while missing the one row that mattered.
 */
async function drainDomainPages(
  fetchPage: (page: number) => Promise<EmailDomain[] | ServerError>,
): Promise<EmailDomain[] | ServerError> {
  const all: EmailDomain[] = [];
  for (let page = 0; page < DOMAIN_MAX_PAGES; page++) {
    const result = await fetchPage(page);
    if ("isServerError" in result && result.isServerError === true) {
      // Page 0 failing is a real failure the caller must classify (capability
      // vs outage). A later page failing leaves a short list, which is the
      // pre-existing behaviour — better than discarding what we already have.
      return page === 0 ? result : all;
    }
    const rows = result as EmailDomain[];
    all.push(...rows);
    if (rows.length < DOMAIN_PAGE_LIMIT) return all;
  }
  // Never silently: a truncated list reads exactly like a complete one, which is
  // how the single-page read hid this for so long.
  console.warn(
    `email domains: stopped after ${DOMAIN_MAX_PAGES} pages (${all.length} rows); ` +
      "the list may be incomplete and senders on the missing domains will not resolve",
  );
  return all;
}

/**
 * Every domain row of a company, all pages (the company settings list).
 *
 * `UserDomainsPage` used to read page 0 with the default limit of 10, so a
 * company with an eleventh row never saw it — and "Verify the domain" from an
 * address sent on behalf could not find a DOMAIN row that did exist, offered a
 * create instead, and earned a `domainAlreadyExists` 400 (sender on behalf,
 * 2026-09-29).
 */
export async function getAllEmailDomainsByCompanyHandler(
  companyId: string,
): Promise<EmailDomain[] | ServerError> {
  return drainDomainPages((page) =>
    getEmailDomainsByCompanyHandler(companyId, page, DOMAIN_PAGE_LIMIT),
  );
}

/**
 * Fetch, one by one, the domain rows that senders reference but the page did
 * not return.
 *
 * A sender whose `emailDomainId` has no matching row is dropped by BOTH
 * `selectableSenderAddresses` and `unverifiedSenderAddresses` — it disappears
 * from the picker with no label, and from the sender settings table entirely,
 * where it also loses its delete button. An address you can neither see nor
 * remove is the worst of the shapes this can take.
 *
 * Why top-up rather than "page until exhausted": completeness is only needed
 * for the domains senders actually reference, and this needs no backend change.
 * A paging loop would ALSO have been unsound until the `$sort` fix landed —
 * without a stable order, successive `$skip`/`$limit` calls are not guaranteed
 * to enumerate the collection, so the loop can finish "complete" and still be
 * missing the one row that mattered.
 *
 * Why not simply raise `DOMAIN_PAGE_LIMIT`: `EmailDomainHandling` rejects a
 * limit above 100 client-side, so that change would be a silent no-op that
 * looks like a fix.
 */
async function topUpMissingDomains(
  senders: SenderAddress[],
  domains: EmailDomain[],
  domainAccessDenied: boolean,
): Promise<EmailDomain[]> {
  // No read permission means every single-domain call would 403 too. Firing N
  // of them would turn one denied read into a burst of them.
  if (domainAccessDenied) return domains;

  const present = new Set(domains.map((d) => d.emailDomainId));
  const missing = Array.from(
    new Set(
      senders
        .map((s) => s.emailDomainId)
        .filter((id): id is string => Boolean(id) && !present.has(id)),
    ),
  ).slice(0, DOMAIN_TOPUP_LIMIT);

  // The overwhelmingly common case: nothing to do, no request, no cost.
  if (missing.length === 0) return domains;

  // SEQUENTIAL, and it stops at the first refusal.
  //
  // `GET /email-domain/{id}` is `@RolesAllowed({"OWNER"})`, while the LIST route
  // this complements admits nine roles plus `email-domains:read`. So for a
  // company ADMIN/EDITOR/VIEWER or any EVENT-* collaborator every one of these
  // calls 403s. Firing them in parallel turned one refusal into up to 25
  // round-trips and 25 console errors on the critical path of the send modal,
  // every time it opened.
  //
  // Widening the endpoint's roles is not the fix — its javadoc explains that the
  // by-id and by-event lanes authenticate on different axes. Losing one refusal
  // instead of twenty-five is.
  const fetched: EmailDomain[] = [];
  for (const id of missing) {
    const row = await getEmailDomainByIdHandler(id);
    if (row && "isServerError" in row && row.isServerError === true) {
      // A refusal here is a property of the CALLER, not of this id — every
      // remaining id would be refused identically. Stop.
      break;
    }
    if (row) fetched.push(row as EmailDomain);
  }

  return [...domains, ...fetched];
}

export async function getSenderAddressesByEventHandler(
  eventId: string,
  fields: string = DEFAULT_FIELDS
): Promise<SenderAddress[] | ServerError> {
  try {
    // Validate eventId
    if (!isValidObjectId(eventId)) {
      throw new Error("validationError");
    }

    // Validate fields
    if (!fields || fields.trim().length === 0) {
      throw new Error("validationError");
    }

    return await getSenderAddressesByEvent(eventId, fields);
  } catch (error) {
    console.error("Error getting sender addresses by event:", error);
    return handleErrorServer(error);
  }
}

export async function getPrimarySenderAddressForEventHandler(
  eventId: string,
  fields: string = DEFAULT_FIELDS
): Promise<SenderAddress | ServerError> {
  try {
    // Validate eventId
    if (!isValidObjectId(eventId)) {
      throw new Error("validationError");
    }

    // Validate fields
    if (!fields || fields.trim().length === 0) {
      throw new Error("validationError");
    }

    return await getPrimarySenderAddressForEvent(eventId, fields);
  } catch (error) {
    console.error("Error getting primary sender address for event:", error);
    return handleErrorServer(error);
  }
}

// ── Template-scoped handlers ──

const TEMPLATE_DEFAULT_FIELDS = "senderAddressId,emailDomainId,templateId,email,primary,alias";

export async function createTemplateSenderAddressHandler(
  templateId: string,
  emailDomainId: string,
  email: string,
  alias?: string
): Promise<string | ServerError> {
  try {
    if (!isValidObjectId(templateId)) {
      throw new Error("validationError");
    }
    if (!isValidObjectId(emailDomainId)) {
      throw new Error("validationError");
    }
    if (!email || email.trim().length === 0) {
      throw new Error("validationError");
    }
    if (!EMAIL_LOCAL_PART_REGEX.test(email)) {
      throw new Error("validationError");
    }

    return await createTemplateSenderAddress(templateId, emailDomainId, email, alias);
  } catch (error) {
    console.error("Error creating template sender address:", error);
    return handleErrorServer(error);
  }
}

export async function updateTemplateSenderAddressHandler(
  senderAddressId: string,
  templateId: string,
  alias: string | null
): Promise<void | ServerError> {
  try {
    if (!isValidObjectId(senderAddressId)) {
      throw new Error("validationError");
    }
    if (!isValidObjectId(templateId)) {
      throw new Error("validationError");
    }

    await updateTemplateSenderAddress(senderAddressId, templateId, alias);
  } catch (error) {
    console.error("Error updating template sender address:", error);
    return handleErrorServer(error);
  }
}

export async function setTemplatePrimarySenderAddressHandler(
  senderAddressId: string,
  templateId: string
): Promise<void | ServerError> {
  try {
    if (!isValidObjectId(senderAddressId)) {
      throw new Error("validationError");
    }
    if (!isValidObjectId(templateId)) {
      throw new Error("validationError");
    }

    await setTemplatePrimarySenderAddress(senderAddressId, templateId);
  } catch (error) {
    console.error("Error setting template primary sender address:", error);
    return handleErrorServer(error);
  }
}

export async function deleteTemplateSenderAddressHandler(
  senderAddressId: string,
  templateId: string
): Promise<boolean | ServerError> {
  try {
    if (!isValidObjectId(senderAddressId)) {
      throw new Error("validationError");
    }
    if (!isValidObjectId(templateId)) {
      throw new Error("validationError");
    }

    return await deleteTemplateSenderAddress(senderAddressId, templateId);
  } catch (error) {
    console.error("Error deleting template sender address:", error);
    return handleErrorServer(error);
  }
}

export async function getSenderAddressesByTemplateHandler(
  templateId: string,
  fields: string = TEMPLATE_DEFAULT_FIELDS
): Promise<SenderAddress[] | ServerError> {
  try {
    if (!isValidObjectId(templateId)) {
      throw new Error("validationError");
    }
    if (!fields || fields.trim().length === 0) {
      throw new Error("validationError");
    }

    return await getSenderAddressesByTemplate(templateId, fields);
  } catch (error) {
    console.error("Error getting sender addresses by template:", error);
    return handleErrorServer(error);
  }
}

export async function getTemplateEmailsMetaInformation(
  templateId: string,
): Promise<EmailsMetaInformation | ServerError> {
  const companyId = await getCookie("companyId");

  try {
    if (!companyId) {
      throw new Error("Company ID is not set in cookies.");
    }

    const [senderAddresses, emailDomains] = await Promise.all([
      getSenderAddressesByTemplateHandler(templateId),
      // Same paging trap as the event lane — a company has MORE domains than a
      // single event, so the default of 10 bit here first.
      drainDomainPages((page) =>
        getEmailDomainsByCompanyHandler(companyId, page, DOMAIN_PAGE_LIMIT),
      ),
    ]);

    if ("isServerError" in emailDomains && emailDomains.isServerError === true)
      return emailDomains;

    // A failed sender-address fetch used to be swallowed into an empty list
    // ("endpoint isn't deployed yet" fallback) — the page then rendered a
    // silent empty state on a real 5xx. Propagate it like the domains
    // failure (BUGS.md M4/M12, FIXED 2026-07-05 W2-E).
    if ("isServerError" in senderAddresses && senderAddresses.isServerError === true)
      return senderAddresses;

    // This lane propagates BOTH failures (including 403) instead of degrading —
    // the company-template screens have no second reason to exist without them.
    const senders = senderAddresses as SenderAddress[];
    return {
      senderAddresses: senders,
      // Same hole as the event lane, and a company legitimately has MORE
      // domains than a single event — so this is where a sender is most likely
      // to reference a domain the page did not return. `false` because a
      // domain-read failure has already returned above: reaching here means the
      // read succeeded, so the per-domain calls are worth making.
      emailDomains: await topUpMissingDomains(
        senders,
        emailDomains as EmailDomain[],
        false,
      ),
      senderAccessDenied: false,
      domainAccessDenied: false,
    };
  } catch (error) {
    return handleErrorServer(error);
  }
}